import { API, Endpoints, Project, Team } from "@brg/api";
import { useResizedImage, MEDIA } from "@brg/ui";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppTester, getCurrentAppName, getCurrentContextName } from "../../../supporting";
import { getCardNumber } from "../../helpers/utils/getCardNumber";
import styled, { css} from "styled-components";
import useQueryState from "shared/hooks/useQueryState";
import { isServer } from 'shared/constants/isServer';
import {
    CARD_FRAME_DH,
    CARD_FRAME_DW,
    CARD_FRAME_MOBILE_DW_C,
    CARD_FRAME_MOBILE_DW_K,
    CARD_FRAME_MOBILE_HORIZONTAL_DH_COEF,
    CARD_FRAME_TABLET_COEF,
    getHorizontalCardHeight,
    wrapperWidthHorizontal,
} from "./const";
import { useMediaQuoryForCard } from "./useMediaQuoryForCard";
import { LayoutConst } from "core/modules/layout.module";
import { useRouteMatch } from "react-router-dom";
import { LinkTo } from "shared/constants/linkTo";

const CardComponent = isServer ? {} : require('adaptive-cards');

const IS_HORIZONTAL = true;

export const CardFrameWrapper = styled.div<{ shouldUseNewBreakpoints?: boolean }>`
    position: relative;
    width: ${wrapperWidthHorizontal.defaultWidth}px;
    height: ${getHorizontalCardHeight(wrapperWidthHorizontal.defaultWidth)}px;
    overflow: hidden;
    background: white;
    box-sizing: border-box;
    flex-shrink: 0;

    ${({ shouldUseNewBreakpoints }) => shouldUseNewBreakpoints ? css`
        @media ${MEDIA.LAPTOP} {
            max-width: 100%;
            width: ${wrapperWidthHorizontal.laptop}px;
            height: ${getHorizontalCardHeight(wrapperWidthHorizontal.laptop)}px;
        }
    ` : css`
        @media (max-width: ${LayoutConst.screen.l}px) {
            max-width: 100%;
            width: ${wrapperWidthHorizontal.laptop}px;
            height: ${getHorizontalCardHeight(wrapperWidthHorizontal.laptop)}px;
        }
    `};

    @media ${MEDIA.MOBILE} {
        width: 100%;
        height: 100%;
        min-height: 0;
    }
`;

export const WarholCardCoverBg = styled(CardFrameWrapper)``;

export const WarholCardCoverEditBg = styled(WarholCardCoverBg)`
    box-shadow: 0px 4px 4px rgba(61, 59, 89, 0.06);
`;

export const WarholCardCoverFixed: FC<{ project?: Project<'Global'> | Project<'Local'> | Team<'Global'> | Team<'Local'> }> = ({ project }) => {
    const { i18n: { language } } = useTranslation();
    const isMediiiaAstro = AppTester.isMediiiaNature();
    const [{ tab }] = useQueryState();
    const isProjectRoute = !!useRouteMatch(LinkTo.project);

    //@ts-ignore
    const templateType = useMemo(() => (project?.additionalInfo?.['templateType'] && JSON.parse(project?.additionalInfo?.['templateType'])), [project]);
    //@ts-ignore
    const templateData = useMemo(() => (project?.additionalInfo?.['templateData'] && API.toLang(JSON.parse(project?.additionalInfo?.['templateData']), language)), [project, language]);
    const appName = getCurrentAppName();
    const contextName = getCurrentContextName();

    const images = useMemo(() => project?.titleImages ?? [], [project?.titleImages]);
    const [img, containerRef] = useResizedImage(images);
    const { breakPoint } = useMediaQuoryForCard({ newBreakpoints: true });
    const isLaptop = breakPoint === 'laptop'
    const isTablet = breakPoint === 'tablet';
    const isMobile = breakPoint === 'mobile';

    const shouldUseNewBreakpoints = isProjectRoute;

    //@ts-ignore
    const cWidth = containerRef?.current?.clientWidth;
    const [resizeTick, setResizeTick] = useState(Date.now());

    useEffect(() => {
        setResizeTick(Date.now());
    }, [isMobile, cWidth]);

    const templateTypeName = useMemo(() => {
        if (isMediiiaAstro && tab === 'floral') {
            return 'FlowerHoroscopeCardA4';
        }

        const name = templateType?.nameComponent || 'WarholCardA4L';

        if (templateType?.orientation === 'horizontal' && !name.endsWith('L')) {
            return `${name}L`;
        }

        return name;
    }, [templateType?.nameComponent, templateType?.orientation, isMediiiaAstro, tab]);

    const cardNumber = useMemo(() => getCardNumber(templateData?.number), [templateData?.number]);

    const dw = useMemo(() => {
        if (isMobile && cWidth) {
            return CARD_FRAME_MOBILE_DW_K * cWidth + CARD_FRAME_MOBILE_DW_C;
        }
        if (isLaptop && shouldUseNewBreakpoints) {
            return CARD_FRAME_DW[+IS_HORIZONTAL] * 0.77
        }

        if (isTablet && cWidth && shouldUseNewBreakpoints) {
            return CARD_FRAME_MOBILE_DW_K * cWidth + 1.9;
        }

        return CARD_FRAME_DW[+IS_HORIZONTAL] * (isTablet ? CARD_FRAME_TABLET_COEF : 1);
    }, [isMobile, cWidth, isTablet, resizeTick, isLaptop, shouldUseNewBreakpoints]);

    const dh = useMemo(() => {
        if (isMobile) {
            return dw * CARD_FRAME_MOBILE_HORIZONTAL_DH_COEF;
        }

        if (isLaptop && shouldUseNewBreakpoints) {
            return CARD_FRAME_DH[+IS_HORIZONTAL] * 0.755
        }

        if (isTablet && shouldUseNewBreakpoints) {
            return CARD_FRAME_DH[+IS_HORIZONTAL] * 0.72;
        }

        return CARD_FRAME_DH[+IS_HORIZONTAL] * (isTablet ? CARD_FRAME_TABLET_COEF : 1);
    }, [dw, isMobile, isTablet, shouldUseNewBreakpoints]);

    const imageUrl = useMemo(() => {
        if (!img?.url) {
            return undefined;
        }
        return Endpoints.files + '/' + img.url;
    }, [img?.url]);

    if (!templateType || !templateData) {
        return null;
    }

    const canRenderCard = !isMobile || Boolean(cWidth);

    return (
        <CardFrameWrapper ref={containerRef} shouldUseNewBreakpoints={shouldUseNewBreakpoints}>
            {canRenderCard && imageUrl && (
                <CardComponent.MasterCard
                    data={{
                        templateType: { ...templateType, link: window.location.hostname },
                        templateData: { ...templateData, link: window.location.hostname },
                        number: cardNumber,
                    }}
                    image={imageUrl}
                    templateType={templateTypeName}
                    appName={appName}
                    contextName={contextName}
                    dw={dw}
                    dh={dh}
                />
            )}
        </CardFrameWrapper>
    );
};
import { API, Endpoints, Project, Team } from "@brg/api";
import { useResizedImage, MEDIA } from "@brg/ui";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppTester, getCurrentAppName, getCurrentContextName } from "../../../supporting";
import { getCardNumber } from "../../helpers/utils/getCardNumber";
import styled, { css} from "styled-components";
import useQueryState from "shared/hooks/useQueryState";
import { isServer } from 'shared/constants/isServer';
import {
    CARD_FRAME_DH,
    CARD_FRAME_DW,
    CARD_FRAME_MOBILE_DW_C,
    CARD_FRAME_MOBILE_DW_K,
    CARD_FRAME_MOBILE_HORIZONTAL_DH_COEF,
    CARD_FRAME_TABLET_COEF,
    getHorizontalCardHeight,
    wrapperWidthHorizontal,
} from "./const";
import { useMediaQuoryForCard } from "./useMediaQuoryForCard";
import { LayoutConst } from "core/modules/layout.module";
import { useRouteMatch } from "react-router-dom";
import { LinkTo } from "shared/constants/linkTo";

const CardComponent = isServer ? {} : require('adaptive-cards');

const IS_HORIZONTAL = true;

export const CardFrameWrapper = styled.div<{ shouldUseNewBreakpoints?: boolean }>`
    position: relative;
    width: ${wrapperWidthHorizontal.defaultWidth}px;
    height: ${getHorizontalCardHeight(wrapperWidthHorizontal.defaultWidth)}px;
    overflow: hidden;
    background: white;
    box-sizing: border-box;
    flex-shrink: 0;

    ${({ shouldUseNewBreakpoints }) => shouldUseNewBreakpoints ? css`
        @media ${MEDIA.LAPTOP} {
            max-width: 100%;
            width: ${wrapperWidthHorizontal.laptop}px;
            height: ${getHorizontalCardHeight(wrapperWidthHorizontal.laptop)}px;
        }
    ` : css`
        @media (max-width: ${LayoutConst.screen.l}px) {
            max-width: 100%;
            width: ${wrapperWidthHorizontal.laptop}px;
            height: ${getHorizontalCardHeight(wrapperWidthHorizontal.laptop)}px;
        }
    `};

    @media ${MEDIA.MOBILE} {
        width: 100%;
        height: 100%;
        min-height: 0;
    }
`;

export const WarholCardCoverBg = styled(CardFrameWrapper)``;

export const WarholCardCoverEditBg = styled(WarholCardCoverBg)`
    box-shadow: 0px 4px 4px rgba(61, 59, 89, 0.06);
`;

export const WarholCardCoverFixed: FC<{ project?: Project<'Global'> | Project<'Local'> | Team<'Global'> | Team<'Local'> }> = ({ project }) => {
    const { i18n: { language } } = useTranslation();
    const isMediiiaAstro = AppTester.isMediiiaNature();
    const [{ tab }] = useQueryState();
    const isProjectRoute = !!useRouteMatch(LinkTo.project);

    //@ts-ignore
    const templateType = useMemo(() => (project?.additionalInfo?.['templateType'] && JSON.parse(project?.additionalInfo?.['templateType'])), [project]);
    //@ts-ignore
    const templateData = useMemo(() => (project?.additionalInfo?.['templateData'] && API.toLang(JSON.parse(project?.additionalInfo?.['templateData']), language)), [project, language]);
    const appName = getCurrentAppName();
    const contextName = getCurrentContextName();

    const images = useMemo(() => project?.titleImages ?? [], [project?.titleImages]);
    const [img, containerRef] = useResizedImage(images);
    const { breakPoint } = useMediaQuoryForCard({ newBreakpoints: true });
    const isLaptop = breakPoint === 'laptop'
    const isTablet = breakPoint === 'tablet';
    const isMobile = breakPoint === 'mobile';

    const shouldUseNewBreakpoints = isProjectRoute;

    //@ts-ignore
    const cWidth = containerRef?.current?.clientWidth;
    const [resizeTick, setResizeTick] = useState(Date.now());

    useEffect(() => {
        setResizeTick(Date.now());
    }, [isMobile, cWidth]);

    const templateTypeName = useMemo(() => {
        if (isMediiiaAstro && tab === 'floral') {
            return 'FlowerHoroscopeCardA4';
        }

        const name = templateType?.nameComponent || 'WarholCardA4L';

        if (templateType?.orientation === 'horizontal' && !name.endsWith('L')) {
            return `${name}L`;
        }

        return name;
    }, [templateType?.nameComponent, templateType?.orientation, isMediiiaAstro, tab]);

    const cardNumber = useMemo(() => getCardNumber(templateData?.number), [templateData?.number]);

    const dw = useMemo(() => {
        if (isMobile && cWidth) {
            return CARD_FRAME_MOBILE_DW_K * cWidth + CARD_FRAME_MOBILE_DW_C;
        }
        if (isLaptop && shouldUseNewBreakpoints) {
            return CARD_FRAME_DW[+IS_HORIZONTAL] * 0.77
        }

        if (isTablet && cWidth && shouldUseNewBreakpoints) {
            return CARD_FRAME_MOBILE_DW_K * cWidth + 1.9;
        }

        return CARD_FRAME_DW[+IS_HORIZONTAL] * (isTablet ? CARD_FRAME_TABLET_COEF : 1);
    }, [isMobile, cWidth, isTablet, resizeTick, isLaptop, shouldUseNewBreakpoints]);

    const dh = useMemo(() => {
        if (isMobile) {
            return dw * CARD_FRAME_MOBILE_HORIZONTAL_DH_COEF;
        }

        if (isLaptop && shouldUseNewBreakpoints) {
            return CARD_FRAME_DH[+IS_HORIZONTAL] * 0.755
        }

        if (isTablet && shouldUseNewBreakpoints) {
            return CARD_FRAME_DH[+IS_HORIZONTAL] * 0.72;
        }

        return CARD_FRAME_DH[+IS_HORIZONTAL] * (isTablet ? CARD_FRAME_TABLET_COEF : 1);
    }, [dw, isMobile, isTablet, shouldUseNewBreakpoints]);

    const imageUrl = useMemo(() => {
        if (!img?.url) {
            return undefined;
        }
        return Endpoints.files + '/' + img.url;
    }, [img?.url]);

    if (!templateType || !templateData) {
        return null;
    }

    const canRenderCard = !isMobile || Boolean(cWidth);

    return (
        <CardFrameWrapper ref={containerRef} shouldUseNewBreakpoints={shouldUseNewBreakpoints}>
            {canRenderCard && imageUrl && (
                <CardComponent.MasterCard
                    data={{
                        templateType: { ...templateType, link: window.location.hostname },
                        templateData: { ...templateData, link: window.location.hostname },
                        number: cardNumber,
                    }}
                    image={imageUrl}
                    templateType={templateTypeName}
                    appName={appName}
                    contextName={contextName}
                    dw={dw}
                    dh={dh}
                />
            )}
        </CardFrameWrapper>
    );
};
